/**
 * EventProductStreamingService
 * Service for auto-paginated streaming of event products.
 * Uses AsyncGenerator pattern for progressive data loading from the server.
 */
/**
 * EventProductStreamingService
 * Streams event products from the server in batches using auto-pagination.
 * Each yield returns a batch of products plus the total count.
 *
 * @example
 * ```typescript
 * const service = new EventProductStreamingService(shopService);
 * for await (const chunk of service.streamEventProducts(eventId, 10)) {
 *   console.log(`Received ${chunk.items.length} products, total: ${chunk.totalCount}`);
 * }
 * ```
 */
export class EventProductStreamingService {
    constructor(shopService) {
        this.shopService = shopService;
    }
    /**
     * Stream event products with server-side pagination
     * @param eventId - The event ID to fetch products for
     * @param batchSize - Number of products per batch (default: 10)
     * @yields EventProductChunk with items and totalCount
     */
    async *streamEventProducts(eventId, batchSize = 10) {
        let skip = 0;
        let hasMore = true;
        while (hasMore) {
            const response = await this.shopService.getEventProductsRaw(eventId, skip, batchSize);
            const items = response.data ?? [];
            const totalCount = response.totalCount ?? 0;
            hasMore = response.hasMore ?? false;
            if (items.length > 0) {
                yield { items, totalCount };
            }
            skip += batchSize;
            // Safety: stop if server returned no items
            if (items.length === 0)
                break;
        }
    }
}
//# sourceMappingURL=EventProductStreamingService.js.map